// wedding-editorial-cinema-preview — w29 "Kinokadr" card-preview.
//
// Cinematic editorial wedding. Outer frame is an antique gold foil
// gradient; inside, a near-black card with a thin gold hairline, a
// rectangular "film-still" photo medallion as the focal element, and
// names + caption in the template's Cormorant Garamond. Mirrors the
// rose-gold pattern (outer frame + inner card + medallion + names +
// caption) so the catalog reads cohesively.
import { Photo, type LayoutCtx } from "./_shared";

export function WeddingEditorialCinemaPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const FOIL = `linear-gradient(135deg, #6E5824 0%, ${accent} 22%, #E8C880 45%, ${accent} 65%, #6E5824 100%)`;
  const [a, b] = names.split(/\s*&\s*/);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Cinema corner ticks */}
        <span aria-hidden style={{ position: "absolute", top: "4%", left: "4%", width: 10, height: 10, borderTop: `1px solid ${accent}`, borderLeft: `1px solid ${accent}` }} />
        <span aria-hidden style={{ position: "absolute", top: "4%", right: "4%", width: 10, height: 10, borderTop: `1px solid ${accent}`, borderRight: `1px solid ${accent}` }} />
        <span aria-hidden style={{ position: "absolute", bottom: "4%", left: "4%", width: 10, height: 10, borderBottom: `1px solid ${accent}`, borderLeft: `1px solid ${accent}` }} />
        <span aria-hidden style={{ position: "absolute", bottom: "4%", right: "4%", width: 10, height: 10, borderBottom: `1px solid ${accent}`, borderRight: `1px solid ${accent}` }} />

        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* Film-still rectangular medallion. */}
        <div
          style={{
            width: "62%",
            aspectRatio: "16 / 10",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: FOIL,
              padding: "3%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                background: ink,
                padding: "2%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(ellipse at 50% 50%, ${accent}55, ${ink} 75%)`,
                      }}
                    />
                  }
                />
                {/* Subtle vignette bottom */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.45) 100%)`,
                  }}
                />
                {/* Film sprocket strip top + bottom */}
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `repeating-linear-gradient(to right, ${ink} 0, ${ink} 3px, transparent 3px, transparent 6px)`,
                  }}
                />
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `repeating-linear-gradient(to right, ${ink} 0, ${ink} 3px, transparent 3px, transparent 6px)`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Names — Cormorant italic across an ampersand. */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 30,
            lineHeight: 1.0,
            color: ink,
            fontWeight: 500,
          }}
        >
          {a ?? names}
        </div>
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 22,
            lineHeight: 1.0,
            color: accent,
            margin: "2px 0",
          }}
        >
          &amp;
        </div>
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 30,
            lineHeight: 1.0,
            color: ink,
            fontWeight: 500,
          }}
        >
          {b ?? ""}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TO'Y MAROSIMI · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
